import { useBranch } from "../contexts/BranchContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import { motion } from "framer-motion";
import { Mail, ArrowRight } from "lucide-react";
import { branchGalleryUrl } from "../utils/branchGallery";

const heroImages = {
  caddebostan: branchGalleryUrl("caddebostan", 7),
  zeytinburnu: branchGalleryUrl("zeytinburnu", 7),
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const FRANCHISE_EMAIL = "zeytinburnu@tepsiet.com";
const MAIL_SUBJECT = "Franchise Başvurusu";
const MAIL_BODY = `Merhaba Tepsi Et Ekibi,

Franchise başvurusu için yazıyorum. Tepsi Et markasıyla kendi şubemi açmak istiyorum.

Bilgilerim:
Ad Soyad: 
Telefon: 
Şehir: 
Tercih Edilen Lokasyon: 

Detaylı bilgi almak istiyorum.

Saygılarımla,`;

export default function FranchisePage() {
  const { selectedBranch } = useBranch();
  const { theme } = useTheme();
  const { t } = useLang();
  const dk = theme === "dark";
  const textH = dk ? "text-white" : "text-[#1A1A1A]";
  const textP = dk ? "text-[#999]" : "text-[#666]";
  const cardBg = dk ? "bg-[#121212]" : "bg-white";
  const borderS = dk ? "border-white/5" : "border-black/5";

  const mailtoLink = `mailto:${FRANCHISE_EMAIL}?subject=${encodeURIComponent(MAIL_SUBJECT)}&body=${encodeURIComponent(MAIL_BODY)}`;

  return (
    <div data-testid="franchise-page" className="min-h-screen">
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <div className="absolute inset-0 bg-[#121212]">
          <img src={heroImages[selectedBranch]} alt="Franchise" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-end pb-12 md:pb-16 max-w-[1200px] mx-auto px-6">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] mb-3">{t.franchisePage.label}</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white">
            {t.franchisePage.title1} <span className="italic text-[#8B1A1A]">{t.franchisePage.title2}</span>
          </motion.h1>
        </div>
      </section>

      <section className="py-20 md:py-32 max-w-[900px] mx-auto px-6" data-testid="franchise-content">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className={`font-heading text-3xl sm:text-4xl ${textH} mb-8 leading-tight`}>
            {t.franchisePage.joinTitle1} <span className="italic text-[#8B1A1A]">{t.franchisePage.joinTitle2}</span>
          </h2>
          <div className={`space-y-6 font-body text-base ${textP} leading-relaxed mb-12`}>
            <p>{t.franchisePage.desc1}</p>
            <p>{t.franchisePage.desc2}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {t.franchisePage.advantages.map((item, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className={`border ${borderS} p-6 ${cardBg} hover:border-[#8B1A1A]/30 transition-colors`}>
                <h3 className={`font-heading text-lg ${textH} mb-2`}>{item.title}</h3>
                <p className={`font-body text-sm ${textP}`}>{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className={`text-center border ${borderS} p-12 ${cardBg}`}>
            <Mail className="w-10 h-10 text-[#8B1A1A] mx-auto mb-6" />
            <h3 className={`font-heading text-2xl ${textH} mb-4`}>{t.franchisePage.applyTitle}</h3>
            <p className={`font-body text-sm ${textP} mb-8 max-w-md mx-auto`}>{t.franchisePage.applyDesc}</p>
            <a href={mailtoLink} data-testid="franchise-apply-btn" className="inline-flex items-center gap-3 border border-[#8B1A1A] px-10 py-4 text-sm font-body uppercase tracking-[0.2em] text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white transition-all duration-300">
              <Mail className="w-4 h-4" /> {t.franchisePage.applyBtn} <ArrowRight className="w-4 h-4" />
            </a>
            <p className={`font-body text-xs ${dk ? "text-[#666]" : "text-[#aaa]"} mt-6`}>
              {t.franchisePage.orWrite} <a href={`mailto:${FRANCHISE_EMAIL}`} className="text-[#8B1A1A] underline">{FRANCHISE_EMAIL}</a> {t.franchisePage.toAddress}
            </p>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
